"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import { GroupRow } from "./GroupRow";
import { DataRow } from "./DataRow";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { colorSettingDetection, darkTextureWrapper, renderHeaderConditionally } from "../../utils/functions";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import arrowUpDark from "../../images/arrow-up--dark.svg";
import arrowUpLight from "../../images/arrow-up--light.svg";
import { Dropdown } from "../../component/Dropdown/Dropdown";
import { layoutHeaderClasses, layoutHeaderBigClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
export type TableType = "basic" | "grouped";
export interface TableRow {
  rowTitle?: string; // for basic rows
  dataCells: string[];
}
export type ChildRow = {
  type: "rowTitle";
  title: string;
} | {
  type: "rowData";
  dataCells: string[];
};
export interface TableGroup {
  groupRowTitle: string;
  isExpandedByDefault?: boolean;
  childRows: ChildRow[];
}
export interface DataTableProps {
  eyebrow?: string;
  header: string;
  subheader?: string;
  isHeaderBig?: boolean;
  headerElement?: "h1" | "h2";
  isWrapped?: boolean;
  presetTheme?: string;
  description?: string;
  tableType: TableType;
  columnTitles: string[];
  tableRows: TableRow[] | TableGroup[];
  resetAllLabel?: string;
  enableCompareDropdown: boolean;
  compareDropdownLabel: string;
  footerLinkText?: string;
}
const dataTableHeaderClasses = ["text-gray-m9", "text-[2.5rem]", "font-light", "leading-[3rem]", "tracking-[0.8px]", "!mb-8", "t2-dark:text-white", "text-balance"].join(" ");
const dataTableHeaderBigClasses = ["border-l-[10px]", "border-solid", "border-l-yellow-m10", "!pl-5", "mt-2.5", "text-gray-m9", "text-[2.5rem]", "font-light", "leading-[3rem]", "tracking-[0.8px]", "!mb-8", "t2-dark:text-white", "text-balance"].join(" ");
const thBaseClasses = ["text-gray-m10 text-[1.375rem] font-bold leading-7", "py-2 px-6 border-b border-b-gray-m10 text-center", "sticky top-0 z-[3]", "bg-white t3:bg-gray-m10 t4:bg-blue-trmb t5:bg-blue-dnavy t6:bg-transparent t7:bg-[#1b2023]", "t2-dark:border-b-white t2-dark:text-white", "min-w-[250px]"].join(" ");
const eyebrowClasses = layoutEyebrowClasses;
const backToTopClasses = ["text-blue-trmb uppercase font-bold", "text-sm sm:text-lg", "leading-[1.125rem] sm:leading-7", "tracking-[0.7px] sm:tracking-[1.44px]", "flex py-6 px-5 no-underline items-center gap-2", "hover:underline", "focus:font-bold focus:outline-none focus:underline", "active:font-bold active:outline-none active:underline", "t2-dark:!text-yellow-m10"].join(" ");
export const LayoutDataTable = t0 => {
  const $ = _c(46);
  const {
    eyebrow,
    header,
    subheader,
    isHeaderBig,
    headerElement: t1,
    description,
    isWrapped,
    presetTheme,
    tableType,
    columnTitles,
    tableRows,
    resetAllLabel,
    enableCompareDropdown: t2,
    compareDropdownLabel,
    footerLinkText
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const enableCompareDropdown = t2 === undefined ? false : t2;
  const generateStableId = _temp;
  const uniqueId = generateStableId(header);
  const wrapperClasses = isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : "";
  const t3 = presetTheme || "";
  let t4;
  if ($[0] !== t3) {
    t4 = colorSettingDetection(t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const colorSetting = t4;
  const mainClasses = `py-16 sm:py-[100px] ${presetTheme || ""} ${colorSetting}`;
  const headerClasses = isHeaderBig ? dataTableHeaderBigClasses : dataTableHeaderClasses;
  const t5 = header || "";
  let t6;
  if ($[2] !== headerClasses || $[3] !== headerElement || $[4] !== t5) {
    t6 = renderHeaderConditionally(headerClasses, t5, headerElement);
    $[2] = headerClasses;
    $[3] = headerElement;
    $[4] = t5;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const finalHeader = t6;
  const t7 = presetTheme || "";
  let t8;
  if ($[6] !== t7) {
    t8 = darkTextureWrapper(t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const componentStyles = t8;
  let t9;
  if ($[8] !== columnTitles) {
    t9 = columnTitles.map(_temp2);
    $[8] = columnTitles;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const [visibleColumns, setVisibleColumns] = useState(t9);
  let t10;
  if ($[10] !== columnTitles) {
    t10 = columnTitles.map(_temp3);
    $[10] = columnTitles;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const dropdownOptions = t10;
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = selectedOptions => {
      if (selectedOptions.length < 1) {
        return;
      }
      setVisibleColumns(selectedOptions);
    };
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const handleVisibleColumnsChange = t11;
  const activeColumnIndexes = visibleColumns?.map(_temp4);
  const columnCount = activeColumnIndexes?.length;
  const scrollMode = columnCount > 4;
  const scrollContentRef = React.useRef(null);
  const scrollbarRef = React.useRef(null);
  const thumbRef = React.useRef(null);
  let t12;
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const content = scrollContentRef.current;
      const thumb = thumbRef.current;
      const scrollbar = scrollbarRef.current;
      if (!content || !thumb || !scrollbar) {
        return;
      }
      let rafId = null;
      const handleScroll = () => {
        if (rafId !== null) {
          return;
        }
        rafId = requestAnimationFrame(() => {
          const scrollableWidth = content.scrollWidth - content.clientWidth;
          const scrollRatio = scrollableWidth > 0 ? content.scrollLeft / scrollableWidth : 0;
          const thumbWidth = content.clientWidth / content.scrollWidth * scrollbar.clientWidth;
          thumb.style.width = `${thumbWidth / 2}px`;
          thumb.style.left = `${scrollRatio * (scrollbar.clientWidth - thumbWidth) * 1.12}px`;
          rafId = null;
        });
      };
      content.addEventListener("scroll", handleScroll, {
        passive: true
      });
      handleScroll();
      return () => {
        content.removeEventListener("scroll", handleScroll);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t13 = [];
    $[13] = t12;
    $[14] = t13;
  } else {
    t12 = $[13];
    t13 = $[14];
  }
  useEffect(t12, t13);
  const {
    width
  } = useScreenSize();
  const isMobile = width < 600;
  const formatChildRows = _temp6;
  const tableClasses = `border-separate border-spacing-0 relative ${scrollMode ? "w-[max-content]" : "w-[max-content] sm:w-full sm:table-fixed"}`;
  let t14;
  if ($[15] !== eyebrow) {
    t14 = eyebrow && <div className={eyebrowClasses}>{eyebrow}</div>;
    $[15] = eyebrow;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== finalHeader) {
    t15 = <div>{finalHeader}</div>;
    $[17] = finalHeader;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  let t16;
  if ($[19] !== subheader) {
    t16 = subheader && <div className={layoutSubheaderClasses}>{subheader}</div>;
    $[19] = subheader;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== description) {
    t17 = description && <CommonDescription description={description} className="t2-layout__description" />;
    $[21] = description;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] !== t14 || $[24] !== t15 || $[25] !== t16 || $[26] !== t17) {
    t18 = <div>{t14}{t15}{t16}{t17}</div>;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== columnTitles || $[29] !== compareDropdownLabel || $[30] !== dropdownOptions || $[31] !== enableCompareDropdown || $[32] !== resetAllLabel || $[33] !== visibleColumns) {
    t19 = enableCompareDropdown && <div className="mt-12 w-full sm:w-auto"><Dropdown label={compareDropdownLabel} options={dropdownOptions} selectedFilters={visibleColumns} onChange={handleVisibleColumnsChange} multiSelect={true} displayResetAll={true} resetAllLabel={resetAllLabel} onResetAll={() => {
        setVisibleColumns(columnTitles.map(_temp7));
      }} /></div>;
    $[28] = columnTitles;
    $[29] = compareDropdownLabel;
    $[30] = dropdownOptions;
    $[31] = enableCompareDropdown;
    $[32] = resetAllLabel;
    $[33] = visibleColumns;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t18 || $[36] !== t19) {
    t20 = <div><div className="flex flex-col sm:flex-row justify-between items-end mb-10 sm:mb-20">{t18}{t19}</div></div>;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const t21 = tableType === "grouped" && (tableRows as TableGroup[])?.map((group, index) => <GroupRow key={index} title={group?.groupRowTitle} isExpandedByDefault={group?.isExpandedByDefault} childRows={formatChildRows(group?.childRows, activeColumnIndexes)} columnCount={columnCount} tableType={tableType} />);
  const t22 = tableType === "basic" && (tableRows as TableRow[])?.map((row_1, index_0) => <DataRow key={index_0} title={row_1?.rowTitle} dataCells={activeColumnIndexes?.map(colIndex_1 => row_1?.dataCells?.[colIndex_1] ?? "")} columnCount={columnCount} />);
  let t23;
  if ($[38] !== t21 || $[39] !== t22) {
    t23 = <tbody>{t21}{t22}</tbody>;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== colorSetting || $[42] !== columnCount || $[43] !== footerLinkText || $[44] !== uniqueId) {
    t24 = footerLinkText && <tfoot><tr><td className="!p-0 border-t border-t-gray-m10 t2-dark:border-t-white" colSpan={columnCount}><a href={`#data-table-top-${uniqueId}`} className={backToTopClasses}><Image src={colorSetting === "t2-color-setting--dark" ? arrowUpDark : arrowUpLight} alt="Back to top" className="w-3.5 h-3.5" />{footerLinkText}</a></td></tr></tfoot>;
    $[41] = colorSetting;
    $[42] = columnCount;
    $[43] = footerLinkText;
    $[44] = uniqueId;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  return <div className={mainClasses} style={componentStyles} id={`data-table-top-${uniqueId}`} data-testid="layout-data-table"><div className={wrapperClasses}>{t20}<div className="overflow-auto border border-gray-m2 rounded-[10px] relative" ref={scrollContentRef}><table className={tableClasses}><thead><tr>{activeColumnIndexes?.map((colIndex_0, idx) => <th className={`${thBaseClasses} ${scrollMode ? "" : "sm:min-w-0"} ${idx < activeColumnIndexes.length - 1 ? "border-r border-r-gray-m2" : ""}`} style={{
                width: `${100 / columnCount}%`
              }} key={columnTitles[colIndex_0]}>{columnTitles[colIndex_0]}</th>)}</tr></thead>{isMobile && scrollMode && <tr><td colSpan={columnCount} className="p-0"><div className="h-2 bg-[#fafafa] overflow-hidden w-full relative border-x border-gray-m2" ref={scrollbarRef}><div className="absolute h-full bg-[#c1c1c1] rounded left-0 transition-[left] duration-100 ease-linear" ref={thumbRef} /></div></td></tr>}{t23}{t24}</table></div></div></div>;
};
function _temp(text) {
  if (!text) {
    return "data-table-unknown";
  }
  const slug = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return `data-table-${slug}`;
}
function _temp2(_, i) {
  return i.toString();
}
function _temp3(title, i_0) {
  return {
    label: title,
    value: i_0.toString()
  };
}
function _temp4(v) {
  return parseInt(v, 10);
}
function _temp5(row_0) {
  if (row_0.type === "rowData") {
    return row_0?.dataCells?.length > 0;
  }
  return true;
}
function _temp6(childRows, activeColumnIndexes_0) {
  return childRows?.map(row => {
    if (row.type === "rowData") {
      return {
        ...row,
        dataCells: activeColumnIndexes_0?.map(colIndex => row?.dataCells[colIndex])
      };
    }
    return row;
  }).filter(_temp5);
}
function _temp7(__0, i_1) {
  return i_1.toString();
}